import React, { useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CaseMilestoneAction } from 'pages/Cases/hooks/useGetCases';
import { usePostCaseAction } from 'pages/Cases/hooks/usePostCaseAction';
import ModalButton from 'components/ModalButton';
import { useLocation, useNavigate } from 'react-router-dom';

type SummarySaveAsNewVersionProps = {
  action: CaseMilestoneAction;
  caseId: string;
  isOpen: boolean;
  handleClose: () => void;
  consultFormId: string;
};

const SummarySaveAsNewVersion = ({
  action,
  caseId,
  isOpen,
  handleClose,
  consultFormId,
}: SummarySaveAsNewVersionProps): React.ReactNode => {
  const [versionName, setVersionName] = useState('');
  const { postCaseActionMutate, isSuccess, data, postCaseActionIsLoading } =
    usePostCaseAction();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = () => {
    if (versionName) {
      postCaseActionMutate({
        caseId,
        milestoneId: action.milestoneId,
        versionName,
        value: action.value,
        isDraft: true,
        successToastMessage: 'New version saved successfully',
        parentMilestonesActions: [consultFormId],
      });
    }
  };
  useEffect(() => {
    if (isSuccess && data?.data?.actionId) {
      const newPath = `/cases/${caseId}/portfolio-constructions/${data.data.actionId}/summary${location.search}`;
      navigate(newPath, { replace: true });
    }
  }, [isSuccess, data, caseId, location.search]);

  return (
    <Modal
      disablePortal
      disableEnforceFocus
      disableAutoFocus
      open={isOpen}
      onClose={postCaseActionIsLoading ? () => {} : handleClose}
      disableEscapeKeyDown={postCaseActionIsLoading}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box sx={{ outline: 'none', position: 'relative' }}>
        <Backdrop
          open={postCaseActionIsLoading}
          sx={theme => ({
            zIndex: theme.zIndex.modal + 1,
            bgcolor: 'rgba(240, 240, 240, 0.72)',
            backdropFilter: 'blur(2px)',
            pointerEvents: 'auto',
          })}
        >
          <CircularProgress size={44} thickness={3} sx={{ color: '#757575' }} />
        </Backdrop>
        <Box
          sx={theme => ({
            position: 'relative',
            zIndex: 1,
            width: 500,
            bgcolor: '#fff',
            boxShadow: theme.shadows[5],
            borderRadius: '4px',
          })}
        >
          <Grid
            item
            container
            xs={12}
            justifyContent={'space-between'}
            alignItems={'center'}
            p="8px 8px 8px 16px"
          >
            <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>
              Save as new version
            </Typography>
            <Button
              style={{
                minWidth: 'unset',
                padding: '5px',
                marginLeft: 'auto',
              }}
              disabled={postCaseActionIsLoading}
              onClick={handleClose}
            >
              <CloseIcon />
            </Button>
          </Grid>

          <Grid
            item
            container
            px={2}
            sx={{ border: '16px solid #f8f8f8', padding: '16px' }}
          >
            <InputLabel
              htmlFor="versionName"
              sx={{ fontSize: '13px', fontWeight: 400 }}
            >
              Version name
            </InputLabel>

            <TextField
              fullWidth
              id="versionName"
              size="small"
              style={{ paddingTop: '5px' }}
              value={versionName}
              onChange={ev => setVersionName(ev.target.value)}
            />
          </Grid>
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="space-between"
            p={2}
            gap={1}
          >
            <ModalButton
              label="Cancel"
              variant="secondary"
              handleClick={handleClose}
              disabled={postCaseActionIsLoading}
            />
            <ModalButton
              label="Save version"
              handleClick={handleSubmit}
              disabled={postCaseActionIsLoading}
            />
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};

export default SummarySaveAsNewVersion;
